/**
 * Minimal zero-dependency HTML sanitizer for rendering ticket bodies.
 *
 * Content here is self-authored (the tenant writes their own ticket in our
 * editor), so this is defense-in-depth, not the primary trust boundary. When
 * the real backend lands, sanitize server-side on write as well (or store a
 * structured format). Allowlist approach: keep formatting + media, drop
 * scripts, event handlers, and non-safe URLs.
 */

const ALLOWED_TAGS = new Set([
  "P", "BR", "B", "STRONG", "I", "EM", "U", "S", "UL", "OL", "LI",
  "A", "IMG", "VIDEO", "SOURCE", "SPAN", "DIV", "H1", "H2", "H3", "BLOCKQUOTE",
]);
const ALLOWED_ATTRS = new Set([
  "href", "src", "alt", "title", "controls", "style", "target", "rel", "type",
]);

function isSafeUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  // blob: and data: cover our object URLs / inline media; block javascript: etc.
  return (
    v.startsWith("http:") ||
    v.startsWith("https:") ||
    v.startsWith("blob:") ||
    v.startsWith("data:image/") ||
    v.startsWith("data:video/") ||
    v.startsWith("/") ||
    v.startsWith("#") ||
    v.startsWith("mailto:")
  );
}

import { useAuthStore } from "@/lib/store/authStore";

/**
 * Rewrite relative Frappe file URLs (`/files/…`, legacy `/private/files/…`) in
 * inline <img>/<video> to absolute tenant-server URLs, so the media loads from
 * the tenant server (where the file lives) instead of the dashboard origin.
 *
 * Ticket screenshots are uploaded as PUBLIC `/files/` (see
 * `zaviago_backend.api.support.upload_ticket_media`) — the same convention the
 * rest of the dashboard uses for user uploads — so a plain <img> can load them
 * with no auth. Mirrors `${tenantServerUrl}${path}` from PaymentSlipDetail.
 */
export function resolveMediaUrls(html: string): string {
  if (!html) return html;
  const base = useAuthStore.getState().tenantServerUrl;
  if (!base) return html;
  return html.replace(
    /(src|href)="(\/(?:private\/)?files\/[^"]*)"/g,
    (_m, attr: string, path: string) => `${attr}="${base}${path}"`,
  );
}

export interface SplitMessage {
  /** HTML of the text/formatting content, with media removed. */
  textHtml: string;
  /** Each inline <img>/<video> as its own HTML string, to render floating. */
  mediaHtml: string[];
  /** True when there's real text (not just leftover empty <p> tags). */
  hasText: boolean;
}

/**
 * Split a (already-sanitized) message body into its text part and its media
 * (img/video) parts, so media can render as free-floating rounded elements
 * outside the chat bubble instead of boxed inside its colored background.
 */
export function splitMessageMedia(html: string): SplitMessage {
  if (typeof window === "undefined" || !html) {
    return { textHtml: html || "", mediaHtml: [], hasText: !!html };
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const mediaHtml: string[] = [];
  doc.querySelectorAll("img, video").forEach((el) => {
    mediaHtml.push(el.outerHTML);
    el.remove();
  });
  const hasText = (doc.body.textContent ?? "").trim().length > 0;
  return { textHtml: doc.body.innerHTML, mediaHtml, hasText };
}

export function sanitizeHtml(dirty: string): string {
  if (typeof window === "undefined" || !dirty) return "";
  const doc = new DOMParser().parseFromString(dirty, "text/html");

  const walk = (node: Element) => {
    // Iterate over a static copy — we mutate as we go.
    Array.from(node.children).forEach((child) => {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.remove();
        return;
      }
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on") || !ALLOWED_ATTRS.has(name)) {
          child.removeAttribute(attr.name);
        } else if ((name === "href" || name === "src") && !isSafeUrl(attr.value)) {
          child.removeAttribute(attr.name);
        }
      });
      walk(child);
    });
  };

  walk(doc.body);
  return doc.body.innerHTML;
}
